import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { createAccount } from '../../../api/apiServices';
import { useAuth } from '../../../context/useAuth';

export default function SignupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const queryParams = new URLSearchParams(location.search);
  const requestedRole = queryParams.get('role')?.toLowerCase() === 'instructor' ? 'instructor' : 'student';

  const [role, setRole] = useState(requestedRole);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    bio: '',
    specialty: '',
    phoneNumber: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const roleParam = new URLSearchParams(location.search).get('role')?.toLowerCase();
    if (roleParam === 'instructor') {
      setRole('instructor');
    }
  }, [location.search]);

  // Clear errors when typing
  useEffect(() => {
    if (error) setError('');
  }, [formData, role]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Please enter your first and last name.');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const res = await createAccount(
        formData.email.trim().toLowerCase(),
        formData.password,
        role,
        formData.firstName.trim(),
        formData.lastName.trim()
      );

      if (res?.status === true) {
        setSuccess(
          role === 'instructor'
            ? 'Instructor account registered successfully! Redirecting to sign in...'
            : 'Student account created successfully! Redirecting...'
        );

        if (res.newUser?.token) {
          login(res.newUser.token, res.newUser);
          if (role === 'instructor') {
            navigate('/admin', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        } else {
          setTimeout(() => {
            navigate('/login', {
              replace: true,
              state: {
                message: `Account created successfully! Please sign in with your ${role} credentials.`,
                email: formData.email,
              },
            });
          }, 900);
        }
      } else {
        setError(res?.message || 'Failed to create account.');
      }
    } catch (err) {
      console.error('Signup error:', err);
      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'An account with this email already exists or is invalid.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Sidebar */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-950">
        <img
          src={
            role === 'instructor'
              ? 'https://readdy.ai/api/search-image?query=experienced%20educator%20lecturer%20teaching%20coding%20and%20design%20in%20a%20modern%20studio%20laptop%20clean%20creative%20workspace&width=800&height=900&seq=inst1&orientation=portrait'
              : 'https://readdy.ai/api/search-image?query=diverse%20group%20of%20students%20collaborating%20learning%20together%20modern%20classroom%20happy%20motivated%20educational%20environment%20warm%20natural%20light&width=800&height=900&seq=signup1&orientation=portrait'
          }
          alt="Community"
          className="w-full h-full object-cover object-top opacity-35 filter brightness-75"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-teal-950/85 to-slate-950/70 flex flex-col justify-end p-12">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-teal-500 flex items-center justify-center text-white text-xl font-bold shadow-md">
              <i className="ri-graduation-cap-fill" />
            </div>
            <span className="text-2xl font-bold text-white tracking-wide">LearnHub</span>
          </Link>

          {role === 'instructor' ? (
            <>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-300 text-xs font-bold mb-3 w-max">
                <i className="ri-medal-line" /> Instructor Invitation & Portal
              </div>
              <h2 className="text-3xl font-bold text-white mb-3 drop-shadow-md">
                Teach, inspire & earn on LearnHub
              </h2>
              <p className="text-slate-200 text-base leading-relaxed max-w-md drop-shadow-sm">
                Share your knowledge with over 50,000 students, build structured courses, add quizzes, and get paid for your expertise.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-white mb-3 drop-shadow-md">Start learning today</h2>
              <p className="text-slate-200 text-base leading-relaxed max-w-md drop-shadow-sm">
                Join 50,000+ students building real skills with expert-led courses, interactive quizzes, and certificates.
              </p>
            </>
          )}

          <div className="mt-8 space-y-3">
            {(role === 'instructor'
              ? [
                  'Create custom video, text, and slide lessons',
                  'Attach checkpoint quizzes to test student mastery',
                  'Set your own course pricing and promotional discounts',
                  'Real-time student progress tracking & analytics',
                ]
              : [
                  'Expert instructors from top companies',
                  'Interactive quizzes and practical task assignments',
                  'Downloadable verified certificates on completion',
                  'Learn at your own pace, anywhere in Nigeria',
                ]
            ).map((feature) => (
              <div key={feature} className="flex items-center gap-2.5">
                <div className="w-5 h-5 flex items-center justify-center bg-teal-500/30 border border-teal-400/40 rounded-full flex-shrink-0">
                  <i className="ri-check-line text-teal-300 text-xs" />
                </div>
                <span className="text-slate-100 text-sm font-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-teal-600 flex items-center justify-center text-white text-xl font-bold">
                <i className="ri-graduation-cap-fill" />
              </div>
              <span className="text-xl font-bold text-slate-900">LearnHub</span>
            </Link>
          </div>

          {/* Role Toggle Switch */}
          <div className="bg-slate-200/80 p-1 rounded-2xl flex items-center mb-6">
            <button
              type="button"
              onClick={() => setRole('student')}
              className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                role === 'student'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <i className="ri-user-line" /> Student Account
            </button>
            <button
              type="button"
              onClick={() => setRole('instructor')}
              className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                role === 'instructor'
                  ? 'bg-teal-600 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <i className="ri-presentation-line" /> Instructor Account
            </button>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-1">
            {role === 'instructor' ? 'Instructor Registration' : 'Create student account'}
          </h1>
          <p className="text-slate-500 mb-6 text-xs">
            Already have an account?{' '}
            <Link to="/login" className="text-teal-600 font-semibold hover:text-teal-700">
              Sign in
            </Link>
          </p>

          {error && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl mb-5 text-xs flex items-center gap-2">
              <i className="ri-error-warning-line text-sm flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl mb-5 text-xs flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-sm flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">First name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g. John"
                  className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-teal-500 bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Last name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g. Doe"
                  className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-teal-500 bg-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Email address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="you@example.com"
                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-teal-500 bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Minimum 6 characters"
                  className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-teal-500 bg-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Confirm password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  placeholder="Re-enter password"
                  className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-teal-500 bg-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
                >
                  <i className={showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            {role === 'instructor' && (
              <div className="bg-teal-50/70 border border-teal-100 rounded-2xl p-3.5 space-y-3">
                <div className="flex items-center gap-1.5 text-xs font-bold text-teal-900">
                  <i className="ri-information-line text-teal-600" /> Instructor Profile Info
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                    Primary Teaching Topic / Expertise
                  </label>
                  <input
                    type="text"
                    name="specialty"
                    value={formData.specialty}
                    onChange={handleInputChange}
                    placeholder="e.g. Web Development, UI/UX Design, Data Science"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-teal-500 bg-white"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 text-white font-bold rounded-xl transition-all shadow-md cursor-pointer disabled:opacity-50 text-xs flex items-center justify-center gap-2 ${
                role === 'instructor' ? 'bg-teal-600 hover:bg-teal-700' : 'bg-slate-900 hover:bg-slate-800'
              }`}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating {role} account...
                </>
              ) : (
                <>
                  <i className={role === 'instructor' ? 'ri-medal-line' : 'ri-check-line'} />
                  {role === 'instructor' ? 'Join as Instructor' : 'Create Student Account'}
                </>
              )}
            </button>
          </form>

          <p className="text-[11px] text-slate-400 text-center mt-5">
            By signing up, you agree to our Terms of Service and Instructor Code of Conduct.
          </p>

          <div className="mt-4 text-center">
            <Link to="/" className="text-xs text-slate-400 hover:text-slate-600">
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
